import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import Layout from './components/common/Layout';
import Dashboard from './pages/Dashboard';
import League from './pages/League';
import Team from './pages/Team';
import LeagueLayout from './components/layouts/LeagueLayout';
import TeamLayout from './components/layouts/TeamLayout';
import LeagueOverview from './pages/league/LeagueOverview';
import LeagueDraftBoard from './pages/league/LeagueDraftBoard';
import LeagueAwards from './pages/league/LeagueAwards';
import PlayerPage from './pages/league/PlayerPage';
import TeamRoster from './pages/team/TeamRoster';
import TeamPerformance from './pages/team/TeamPerformance';
import RosterLayout from './components/layouts/RosterLayout';
import RosterSeasonView from './pages/team/RosterSeasonView';
import RosterWeekView from './pages/team/RosterWeekView';
import './index.css';

// Redirect component for legacy league URLs
const LeagueRedirect: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  return <Navigate to={`/leagues/espn/${leagueId}`} replace />;
};

// Redirect component for legacy team URLs - needs to lookup league info
const TeamRedirect: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const [redirectPath, setRedirectPath] = React.useState<string | null>(null);
  
  React.useEffect(() => {
    const fetchTeamLeague = async () => {
      try {
        // Use the v1 API to get team data which includes league info
        const response = await fetch(`/api/v1/teams/${teamId}`);
        const teamData = await response.json();
        
        if (teamData.league && teamData.league.id) {
          // Fetch league data to get ESPN league ID
          const leagueResponse = await fetch(`/api/v1/leagues/${teamData.league.id}`);
          const leagueData = await leagueResponse.json();
          
          if (leagueData.espn_league_id) {
            setRedirectPath(`/leagues/espn/${leagueData.espn_league_id}/teams/${teamId}`);
          }
        }
      } catch (error) {
        console.error('Error fetching team league info:', error);
        // Fallback to home page if we can't determine the league
        setRedirectPath('/');
      }
    };
    
    fetchTeamLeague();
  }, [teamId]);
  
  if (!redirectPath) {
    return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-slate-400">Loading...</div>;
  }
  
  return <Navigate to={redirectPath} replace />;
};

// No authentication needed for demo

const App: React.FC = () => {
  return (
    <Router>
      <Routes>
        {/* Home page without layout (no nav bar) */}
        <Route index element={<Dashboard />} />
        
        {/* League routes */}
        <Route path="/leagues/espn/:espnLeagueId" element={<LeagueLayout />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<LeagueOverview />} />
          <Route path="draftboard" element={<LeagueDraftBoard />} />
          <Route path="awards" element={<LeagueAwards />} />
          <Route path="players/:playerId" element={<PlayerPage />} />
        </Route>
        
        {/* Team routes - separate from league layout */}
        <Route path="/leagues/espn/:espnLeagueId/teams/:teamId" element={<TeamLayout />}>
          <Route index element={<Navigate to="roster" replace />} />
          <Route path="roster" element={<RosterLayout />}>
            <Route index element={<Navigate to="week/14" replace />} />
            <Route path="seasonoverview" element={<RosterSeasonView />} />
            <Route path="week/:weekNum" element={<RosterWeekView />} />
          </Route>
          <Route path="performance" element={<TeamPerformance />} />
        </Route>
        
        {/* Legacy routes for backward compatibility */}
        <Route path="/leagues/espn/:espnLeagueId/legacy" element={<League />} />
        <Route path="/teams/:teamId/legacy" element={<Team />} />
        <Route path="/teams/:teamId/roster/legacy" element={<TeamRoster />} />
        
        {/* Legacy team route redirects - lookup league and redirect to new nested structure */}
        <Route path="/teams/:teamId" element={<TeamRedirect />} />
        <Route path="/teams/:teamId/*" element={<TeamRedirect />} />
        
        {/* Fallback route for leagues without provider - redirects to espn */}
        <Route path="/leagues/:leagueId" element={<LeagueRedirect />} />
        
        {/* Other routes with layout */}
        <Route path="/" element={<Layout />}>
          <Route path="leagues" element={<Dashboard />} />
          <Route path="analysis" element={<div className="text-center py-12 text-text-primary">Draft Analysis - Coming Soon</div>} />
          <Route path="scenarios" element={<div className="text-center py-12 text-text-primary">Scenarios - Coming Soon</div>} />
          <Route path="leaderboard" element={<div className="text-center py-12 text-text-primary">Leaderboard - Coming Soon</div>} />
        </Route>
      </Routes>
    </Router>
  );
};



export default App; 